import { PermissionEnum, UserTypeEnum } from '@sr-sdks/api-sdk-axios';
import { useRoles } from '../api/permissions';
import { reduxServices } from '../reducers/reduxServices';
import { useTypedSelector } from '../stateStore';
import {
  formatPermissionName,
  getAllPermissions,
  getDisplayName,
  getSortedRoles,
  hasPermission,
  parseHiddenPermissions,
} from '../utils/helpers/roleAccessHelpers';
import AppTableV2 from './AppTableV2';
import IconWithCircleBackground from './IconWithCircleBackground';

interface RoleAccessTableProps {
  description?: string;
  filterElement?: React.ReactNode;
  /** Hides the built-in heading + description block (e.g. when the caller
   * provides its own heading via `filterElement`). */
  hideDescription?: boolean;
  roleType?: UserTypeEnum;
}

const RoleAccessTable = ({
  description,
  filterElement,
  hideDescription = false,
  roleType,
}: RoleAccessTableProps) => {
  const {
    data: roles,
    error: rolesError,
    isLoading: rolesLoading,
  } = useRoles({
    roleType,
  });

  const isLoading = rolesLoading;

  const configurationEnabled = useTypedSelector(
    reduxServices.setting.selectors.configuration,
  );

  const hiddenPermissionList =
    configurationEnabled?.['teammates.roleAccessTable.hiddenPermissionList'];

  if (rolesError) {
    return null;
  }

  const sortedRoles = getSortedRoles(roles);
  const hiddenPermissions = parseHiddenPermissions(hiddenPermissionList);
  const allPermissions = getAllPermissions(roles, hiddenPermissions);

  return (
    <>
      {!hideDescription && (
        <div className="w-full">
          <h2>Role Access</h2>
          <p className="mb-4 text-gray-600">
            {description ||
              'The table below shows what permissions each teammate access level has within the workspace.'}
          </p>
        </div>
      )}

      <AppTableV2
        id="role-access-table"
        filterElement={filterElement}
        headerRowElement={
          <tr>
            <th className="align-bottom">Permissions</th>
            {sortedRoles.map((role) => (
              <th
                key={role._id}
                className="max-w-[50px] whitespace-normal align-bottom"
              >
                {getDisplayName(role.displayName)}
              </th>
            ))}
          </tr>
        }
        isLoading={isLoading}
        paginationMeta={null}
      >
        {allPermissions.length === 0 && !isLoading ? (
          <tr>
            <td className="py-4 text-center" colSpan={sortedRoles.length + 1}>
              No permissions data available
            </td>
          </tr>
        ) : (
          allPermissions.map((permission) => (
            <tr key={permission}>
              <td className="max-w-[100px] py-2">
                {formatPermissionName(permission)}
              </td>
              {sortedRoles.map((role) => (
                <td
                  key={`${permission}-${role._id}`}
                  className="py-2 text-center"
                >
                  {hasPermission(
                    role.displayName,
                    permission as PermissionEnum,
                    roles,
                  ) ? (
                    <IconWithCircleBackground
                      color="bg-green-100"
                      icon="check"
                      iconColor="text-black"
                    />
                  ) : (
                    <IconWithCircleBackground
                      color="bg-sr-surface-light-red"
                      icon="x"
                      iconColor="text-black"
                    />
                  )}
                </td>
              ))}
            </tr>
          ))
        )}
      </AppTableV2>
    </>
  );
};

export default RoleAccessTable;
